import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { EditUserDialogProps } from '../types';

export function EditUserDialog({
  isDark,
  isOpen,
  onOpenChange,
  formData,
  onFormDataChange,
  onSave,
}: EditUserDialogProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className={cn(
        isDark ? 'bg-[#1E293B] border-slate-700' : 'bg-white'
      )}>
        <DialogHeader>
          <DialogTitle className={cn(
            isDark ? 'text-white' : 'text-[#0F172A]'
          )}>Edit User</DialogTitle>
          <DialogDescription className={cn(
            isDark ? 'text-slate-400' : 'text-slate-500'
          )}>
            Update user information
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="email" className={cn(
              isDark ? 'text-slate-300' : 'text-slate-700'
            )}>Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email || ''}
              onChange={(e) => onFormDataChange(prev => ({ ...prev, email: e.target.value }))}
              className={cn(
                isDark 
                  ? 'bg-slate-800 border-slate-700 text-white' 
                  : 'bg-white border-slate-200'
              )}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="username" className={cn(
              isDark ? 'text-slate-300' : 'text-slate-700'
            )}>Username</Label>
            <Input
              id="username"
              type="text"
              value={formData.username || ''}
              onChange={(e) => onFormDataChange(prev => ({ ...prev, username: e.target.value }))}
              className={cn(
                isDark 
                  ? 'bg-slate-800 border-slate-700 text-white' 
                  : 'bg-white border-slate-200'
              )}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName" className={cn(
                isDark ? 'text-slate-300' : 'text-slate-700'
              )}>First Name</Label>
              <Input
                id="firstName"
                type="text"
                value={formData.firstName || ''}
                onChange={(e) => onFormDataChange(prev => ({ ...prev, firstName: e.target.value }))}
                className={cn(
                  isDark 
                    ? 'bg-slate-800 border-slate-700 text-white' 
                    : 'bg-white border-slate-200'
                )}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName" className={cn(
                isDark ? 'text-slate-300' : 'text-slate-700'
              )}>Last Name</Label>
              <Input
                id="lastName"
                type="text"
                value={formData.lastName || ''}
                onChange={(e) => onFormDataChange(prev => ({ ...prev, lastName: e.target.value }))}
                className={cn(
                  isDark 
                    ? 'bg-slate-800 border-slate-700 text-white' 
                    : 'bg-white border-slate-200'
                )}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="role" className={cn(
              isDark ? 'text-slate-300' : 'text-slate-700'
            )}>Role</Label>
            <Select
              value={formData.role}
              onValueChange={(value: 'user' | 'admin') => onFormDataChange(prev => ({ ...prev, role: value }))}
            >
              <SelectTrigger className={cn(
                isDark 
                  ? 'bg-slate-800 border-slate-700 text-white' 
                  : 'bg-white border-slate-200'
              )}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="isSubscribed"
              checked={formData.isSubscribed || false}
              onChange={(e) => onFormDataChange(prev => ({ ...prev, isSubscribed: e.target.checked }))}
              className="w-4 h-4 rounded border-slate-300"
            />
            <Label htmlFor="isSubscribed" className={cn(
              isDark ? 'text-slate-300' : 'text-slate-700'
            )}>Subscribed</Label>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className={cn(
              isDark 
                ? 'border-slate-700 text-slate-300 hover:bg-slate-800' 
                : 'border-slate-200'
            )}
          >
            Cancel
          </Button>
          <Button onClick={onSave} className="bg-[#0EA5A4] hover:bg-[#0EA5A4]/90">
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
